import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    Title,
    TimeScale
  } from 'chart.js';
  
  // Removed chartjs-adapter-date-fns import as it's not needed
  import { Chart } from 'react-chartjs-2';
  
  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    Title,
    TimeScale
  );
  
  interface DataPoint {
    date: Date;
    value: number;
  }
  
  interface DataSet {
    label: string;
    data: DataPoint[];
    color?: string;
  }
  
  interface GenericLineChartProps {
    title: string;
    datasets: DataSet[];
    aggregationType: 'daily' | 'monthly';
    aggregationMethod: 'sum' | 'avg';
  }
  
  const GenericLineChart = ({ title, datasets, aggregationType, aggregationMethod }: GenericLineChartProps) => {
    const aggregateData = (data: DataPoint[], type: 'daily' | 'monthly') => {
      const aggregated = new Map<string, { sum: number, count: number }>();
      
      data.forEach(point => {
        const date = new Date(point.date);
        const key = type === 'daily' 
          ? date.toISOString().split('T')[0] // YYYY-MM-DD format
          : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM format
        
        if (!aggregated.has(key)) {
          aggregated.set(key, { sum: 0, count: 0 });
        }
        const current = aggregated.get(key)!;
        current.sum += point.value;
        current.count++;
      });

      return Array.from(aggregated).map(([key, { sum, count }]) => ({
        date: new Date(key), // Key is already in YYYY-MM-DD format
        value: aggregationMethod === 'avg' ? sum / count : sum
      })).sort((a, b) => a.date.getTime() - b.date.getTime());
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            position: 'bottom' as const,
          },
          title: {
            display: true,
            text: title,
          },
        },
      };

    const colors = [
      '#98BF06',
      'rgb(0, 125, 173)',
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0'
    ];

    const labels = aggregateData(datasets[0].data, 'daily').map(point => {
      return point.date.toISOString().split('T')[0];
    });
    
    const data = {
      labels,
      datasets: datasets.map((dataset, index) => ({
        label: dataset.label,
        data: aggregateData(dataset.data, 'daily'),
        borderColor: dataset.color || colors[index % colors.length],
        backgroundColor: dataset.color || colors[index % colors.length],
        fill: false,
        tension: 0.4,
        parsing: {
          xAxisKey: 'date',
          yAxisKey: 'value'
        }
      }))
    };

    return <Chart type="line" data={data} options={options} />;
  };
  
  export default GenericLineChart;